// src/lib/credits.js
//
// The Lunexx balance (USD, prepaid) on the client. The server is the truth:
// credits-api for the balance, orders and creator prices; the deposit is a
// call to LunexxCreditDeposit.deposit(orderRef) with EXACTLY the quoted wei,
// from the signed-in wallet. Credits arrive 2 Base blocks after the deposit
// (the scanner, under a minute; larger amounts at finality); nothing here
// changes a balance.

import { encodeFunctionData, parseAbi } from 'viem';
import { writeContract, switchChain, getAccount, getBalance, estimateGas, estimateFeesPerGas } from 'wagmi/actions';
import { verifyBeforeDeposit } from './fundingJourney';
import { supabase } from './supabase';
import { wagmiConfig } from '../config/wagmi';

export const DEPOSIT_ABI = parseAbi(['function deposit(bytes32 orderRef) payable']);

export { formatUsd } from './usd';

/** A supabase.functions error as an Error that KEEPS the server's structured
 *  fields (code, funds, needed, available, balance, …) and the HTTP status —
 *  the Add Funds modal needs them (mission 005, 01b §7). */
export async function functionsError(error) {
  let body = null;
  if (error?.context?.json) { try { body = await error.context.json(); } catch { /* not json */ } }
  const e = new Error();
  if (body && typeof body === 'object') Object.assign(e, body);
  e.message = body?.error || error?.message || 'request failed';
  e.status = error?.context?.status;
  return e;
}

export async function creditsApi(authToken, body) {
  const { data, error } = await supabase.functions.invoke('credits-api', {
    body, headers: { 'X-Lunexx-Auth': `Bearer ${authToken}` },
  });
  if (error) throw await functionsError(error);
  return data;
}

/** Pay a funding order: deposit(orderRef) with the exact quoted wei, from the connected wallet. */
export async function payFundingOrder(order, expectedWallet) {
  const call = order?.call;
  if (!call || call.function !== 'deposit(bytes32)' || !/^0x[0-9a-fA-F]{40}$/.test(call.to || '')
      || !/^0x[0-9a-f]{64}$/.test(call.args?.[0] || '') || !/^[0-9]+$/.test(String(call.value_wei || ''))) {
    throw new Error('This order cannot be paid (malformed).');
  }
  const account = getAccount(wagmiConfig);
  if (!account?.address) throw new Error('Connect your wallet first.');
  if (expectedWallet && account.address.toLowerCase() !== expectedWallet.toLowerCase()) {
    throw new Error('Pay from the wallet you signed in with — the deposit is credited to that account.');
  }
  if (account.chainId !== order.chain_id) await switchChain(wagmiConfig, { chainId: order.chain_id });
  return writeContract(wagmiConfig, {
    address: call.to, abi: DEPOSIT_ABI, functionName: 'deposit', args: [call.args[0]],
    value: BigInt(call.value_wei), chainId: order.chain_id,
  });
}

const DEPOSIT_REFUSALS = {
  malformed: 'This order cannot be paid (malformed).',
  wallet_mismatch: 'Pay from the wallet you signed in with — the deposit is credited to that account.',
  wrong_chain: 'Switch your wallet to Base to pay this order.',
  order_expired: 'This quote has expired. Start a new deposit to get a fresh price.',
  value_mismatch: 'This order cannot be paid (the amount does not match the quote).',
  insufficient_eth: 'Your wallet does not hold enough ETH on Base for this deposit and its network fee.',
  balance_unknown: 'Your wallet balance could not be read. Try again in a moment.',
};

/** Deposit a funding order with the checks of mission 005 (01b §3): the
 *  signed-in wallet, the order's server-selected chain (switched to, then
 *  re-checked), an unexpired order, exactly the quoted value, and value + gas
 *  in the wallet — read on the order's chain, not the connected one. Call it
 *  only from the user's explicit "Deposit" confirmation. Throws an Error with
 *  `code` from verifyBeforeDeposit before anything is signed. */
export async function depositFundingOrder(order, authWallet) {
  const check = async () => {
    const account = getAccount(wagmiConfig);
    let balanceWei = null, gasWei = null;
    if (account?.address) {
      try {
        balanceWei = (await getBalance(wagmiConfig, { address: account.address, chainId: order.chain_id })).value;
        const data = encodeFunctionData({ abi: DEPOSIT_ABI, functionName: 'deposit', args: [order.call.args[0]] });
        const [gas, fees] = await Promise.all([
          estimateGas(wagmiConfig, { account: account.address, to: order.call.to, data, value: BigInt(order.call.value_wei), chainId: order.chain_id }),
          estimateFeesPerGas(wagmiConfig, { chainId: order.chain_id }),
        ]);
        gasWei = gas * (fees.maxFeePerGas ?? fees.gasPrice ?? 0n);
      } catch { /* unknown: refused below */ }
    }
    return { account, v: verifyBeforeDeposit({ authWallet, account: { address: account?.address, chainId: account?.chainId }, order, balanceWei, gasWei }) };
  };
  let { account, v } = await check();
  if (!v.ok && v.code === 'wrong_chain') {
    await switchChain(wagmiConfig, { chainId: order.chain_id });
    ({ account, v } = await check());     // the wallet may have switched account too
  }
  if (!v.ok) throw Object.assign(new Error(DEPOSIT_REFUSALS[v.code] ?? 'This order cannot be paid.'), { code: v.code, beforeSigning: true });
  return writeContract(wagmiConfig, {
    account: account.address, address: order.call.to, abi: DEPOSIT_ABI, functionName: 'deposit', args: [order.call.args[0]],
    value: BigInt(v.valueWei), chainId: order.chain_id,
  });
}

export const PRODUCT_LABELS = {
  chat_reply: { label: 'Chat reply', unit: 'per reply' },
  image_unlock: { label: 'Photo unlock', unit: 'per photo' },
  audio_minute: { label: 'Voice call', unit: 'per minute' },
  video_minute: { label: 'Video call', unit: 'per minute' },
  // creator DEFAULT prices for new rooms and collections (012-01): a prefill
  // for Studio's create forms; each room / collection keeps its own price
  room_ticket: { label: 'Room ticket', unit: 'per ticket' },
  room_replay: { label: 'Replay', unit: 'per replay' },
  collection_unlock: { label: 'Collection', unit: 'per collection' },
};

/** the /balance link that comes back to `path` afterwards */
export const balanceHref = (path) => (path ? `/balance?return=${encodeURIComponent(path)}` : '/balance');
